/**
 * React Query hooks for favourite video and image operations
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toggleVideoFavourite, fetchFavouriteVideos, type FavouriteResponse } from '@/api/videoApi';
import { toggleImageFavourite, fetchFavouriteImages, type FavouriteImageResponse } from '@/api/imageApi';
import videoKeys from '@/api/querry_keys/videoKeys';
import imageKeys from '@/api/querry_keys/imageKeys';

/**
 * Helper function to update video favourite status in cache data
 */
const updateVideoInData = (oldData: any, videoId: string) => {
  if (!oldData) return oldData;

  // Handle array of videos
  if (Array.isArray(oldData)) {
    return oldData.map((video: any) =>
      video.id === videoId
        ? { ...video, is_favourited: !video.is_favourited }
        : video
    );
  }

  // Handle paginated response
  if (oldData.videos && Array.isArray(oldData.videos)) {
    return {
      ...oldData,
      videos: oldData.videos.map((video: any) =>
        video.id === videoId
          ? { ...video, is_favourited: !video.is_favourited }
          : video
      ),
    };
  }

  // Handle infinite query pages
  if (oldData.pages) {
    return {
      ...oldData,
      pages: oldData.pages.map((page: any) => ({
        ...page,
        videos: page.videos?.map((video: any) =>
          video.id === videoId
            ? { ...video, is_favourited: !video.is_favourited }
            : video
        ),
      })),
    };
  }

  return oldData;
};

/**
 * Helper function to update image favourite status in cache data
 */
const updateImageInData = (oldData: any, imageId: string) => {
  if (!oldData) return oldData;

  // Handle array of images
  if (Array.isArray(oldData)) {
    return oldData.map((image: any) =>
      image.id === imageId
        ? { ...image, is_favourited: !image.is_favourited }
        : image
    );
  }

  // Handle paginated response
  if (oldData.images && Array.isArray(oldData.images)) {
    return {
      ...oldData,
      images: oldData.images.map((image: any) =>
        image.id === imageId
          ? { ...image, is_favourited: !image.is_favourited }
          : image
      ),
    };
  }

  // Handle infinite query pages
  if (oldData.pages) {
    return {
      ...oldData,
      pages: oldData.pages.map((page: any) => ({
        ...page,
        images: page.images?.map((image: any) =>
          image.id === imageId
            ? { ...image, is_favourited: !image.is_favourited }
            : image
        ),
      })),
    };
  }

  return oldData;
};

/**
 * Hook to fetch favourite videos
 */
export const useFavouriteVideos = (limit: number = 100, offset: number = 0) => {
  return useQuery({
    queryKey: videoKeys.favourites(limit, offset),
    queryFn: () => fetchFavouriteVideos(limit, offset),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

/**
 * Hook to toggle video favourite status
 */
export const useToggleVideoFavourite = (onSuccess?: (isFavourited: boolean) => void) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (videoId: string): Promise<FavouriteResponse> => {
      return await toggleVideoFavourite(videoId);
    },
    onMutate: async (videoId: string) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: videoKeys.all });

      // Snapshot the previous value for rollback
      const previousData = queryClient.getQueriesData({ queryKey: videoKeys.all });

      // Optimistically update all queries that contain this video using helper
      queryClient.setQueriesData(
        { queryKey: videoKeys.all },
        (oldData: any) => updateVideoInData(oldData, videoId)
      );

      return { previousData };
    },
    onSuccess: (data: FavouriteResponse) => {
      // Call the onSuccess callback with the new favourite status
      if (onSuccess) {
        onSuccess(data.is_favourited);
      }
    },
    onError: (_err: unknown, _videoId: string, context: any) => {
      // Rollback on error
      if (context?.previousData) {
        context.previousData.forEach(([queryKey, data]: [any, any]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      console.error('Error toggling favourite:', _err);
    },
    onSettled: () => {
      // Refetch to ensure data is in sync with server
      queryClient.invalidateQueries({ queryKey: videoKeys.all });
      queryClient.invalidateQueries({ queryKey: videoKeys.favourites() });
    },
  });
};

/**
 * Hook to fetch favourite images
 */
export const useFavouriteImages = (limit: number = 100, offset: number = 0) => {
  return useQuery({
    queryKey: imageKeys.favourites(limit, offset),
    queryFn: () => fetchFavouriteImages(limit, offset),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

/**
 * Hook to toggle image favourite status
 */
export const useToggleImageFavourite = (onSuccess?: (isFavourited: boolean) => void) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (imageId: string): Promise<FavouriteImageResponse> => {
      return await toggleImageFavourite(imageId);
    },
    onMutate: async (imageId: string) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: imageKeys.all });

      // Snapshot the previous value for rollback
      const previousData = queryClient.getQueriesData({ queryKey: imageKeys.all });

      // Optimistically update all queries that contain this image using helper
      queryClient.setQueriesData(
        { queryKey: imageKeys.all },
        (oldData: any) => updateImageInData(oldData, imageId)
      );

      return { previousData };
    },
    onSuccess: (data: FavouriteImageResponse) => {
      // Call the onSuccess callback with the new favourite status
      if (onSuccess) {
        onSuccess(data.is_favourited);
      }
    },
    onError: (_err: unknown, _imageId: string, context: any) => {
      // Rollback on error
      if (context?.previousData) {
        context.previousData.forEach(([queryKey, data]: [any, any]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      console.error('Error toggling image favourite:', _err);
    },
    onSettled: () => {
      // Refetch to ensure data is in sync with server
      queryClient.invalidateQueries({ queryKey: imageKeys.all });
      queryClient.invalidateQueries({ queryKey: imageKeys.favourites() });
    },
  });
};
